import React, { useState, useEffect } from 'react';
import { 
  Typography, Box, Button, Paper, Grid, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, Chip, IconButton, Tabs, Tab, Collapse, Divider,
  Stepper, Step, StepLabel, StepContent, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  FormHelperText, List, ListItem, ListItemText, ListItemSecondaryAction,
  FormControlLabel, Checkbox
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EmailIcon from '@mui/icons-material/Email';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import api, { useAuthenticatedApi } from '../utils/api';
import { useAuth0 } from '@auth0/auth0-react';

const Home = () => {
  const authenticatedApi = useAuthenticatedApi();
  const { user } = useAuth0();
  
  const [stats, setStats] = useState({
    totalReturns: 0,
    pendingReturns: 0,
    completedReturns: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Returns list state
  const [returns, setReturns] = useState([]);
  const [returnsLoading, setReturnsLoading] = useState(true);
  const [returnsError, setReturnsError] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const rowsPerPage = 25; // Number of returns to show per page
  
  // Sorting and searching state
  const [sortField, setSortField] = useState('notification_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for expanded returns and their stages
  const [expandedReturnId, setExpandedReturnId] = useState(null);
  const [returnStages, setReturnStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [latestStages, setLatestStages] = useState({});
  
  // Additional filter states
  const [showReplacementProductSent, setShowReplacementProductSent] = useState(false);
  
  // State for new stage dialog
  const [newStageDialogOpen, setNewStageDialogOpen] = useState(false);
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [stageFormData, setStageFormData] = useState({
    description: '',
    status: 'in_progress',
    handler: '',
    notes: ''
  });
  const [stageError, setStageError] = useState(null);
  const [stageSuccess, setStageSuccess] = useState(null);
  
  // State for return date picker
  const [returnDateDialogOpen, setReturnDateDialogOpen] = useState(false);
  const [selectedReturnForDate, setSelectedReturnForDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [updatingReturnDate, setUpdatingReturnDate] = useState(false);
  
  // State for notification emails
  const [notificationEmails, setNotificationEmails] = useState({});
  const [emailsLoading, setEmailsLoading] = useState({});
  const [availableEmails, setAvailableEmails] = useState([]);
  const [selectedEmailMap, setSelectedEmailMap] = useState({});
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);

  // Function to fetch returns from the API
  const fetchReturns = async () => {
    try {
      setReturnsLoading(true);
      const response = await authenticatedApi.get('/api/returns');
      const fetchedReturns = response.data;
      setReturns(fetchedReturns);
      setReturnsError(null);
      
      // Fetch latest stage for each return
      await fetchLatestStagesForReturns(fetchedReturns);
    } catch (err) {
      console.error('Error fetching returns:', err);
      setReturnsError('Virhe palautusten haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setReturnsLoading(false);
    }
  };
  
  // Function to fetch available emails from authorized users
  const fetchAvailableEmails = async () => {
    try {
      if (!user?.email) {
        console.error('User email not available');
        return;
      }
      
      // Use the authenticated API to get authorized users - using the new non-admin-restricted endpoint
      const response = await authenticatedApi.get(`/api/notification-available-emails?email=${encodeURIComponent(user.email)}`);
      
      // Format the data to match the expected structure
      const formattedEmails = response.data.map(user => ({
        id: user.id,
        email: user.email,
        description: user.is_admin ? 'Admin' : 'Käyttäjä'
      }));
      
      setAvailableEmails(formattedEmails);
      console.log('Fetched authorized users for email selection:', formattedEmails);
    } catch (err) {
      console.error('Error fetching authorized users:', err);
    }
  };
  
  // Function to fetch notification emails for a return
  const fetchNotificationEmails = async (returnId) => {
    if (!returnId) return;
    
    try {
      setEmailsLoading(prev => ({ ...prev, [returnId]: true }));
      const response = await authenticatedApi.get(`/api/returns/${returnId}/notification-emails`);
      setNotificationEmails(prev => ({ ...prev, [returnId]: response.data }));
    } catch (err) {
      console.error('Error fetching notification emails:', err);
    } finally {
      setEmailsLoading(prev => ({ ...prev, [returnId]: false }));
    }
  };
  
  // Function to add a notification email
  const handleAddEmail = async (returnId) => {
    const selectedEmail = selectedEmailMap[returnId];
    if (!selectedEmail) {
      setEmailError('Valitse sähköposti listasta');
      return;
    }
    
    setEmailError(null);
    
    // Check if email is already in the list
    if (notificationEmails[returnId] && notificationEmails[returnId].some(e => e.email === selectedEmail)) {
      setEmailError('Sähköposti on jo lisätty tälle palautukselle');
      return;
    }
    
    try {
      setEmailsLoading(prev => ({ ...prev, [returnId]: true }));
      
      if (!user?.email) {
        setEmailError('Käyttäjän sähköpostia ei löydy. Kirjaudu uudelleen sisään.');
        return;
      }
      
      // Use authenticatedApi with user email as query parameter
      const response = await authenticatedApi.post(
        `/api/returns/${returnId}/notification-emails?email=${encodeURIComponent(user.email)}`, 
        { email: selectedEmail }
      );
      
      if (response.status === 201) {
        // Update the notification emails list
        setNotificationEmails(prev => ({
          ...prev,
          [returnId]: [...(prev[returnId] || []), response.data]
        }));
        setSelectedEmailMap(prev => ({ ...prev, [returnId]: '' }));
        setEmailSuccess('Sähköposti lisätty onnistuneesti');
        
        // Clear success message after 3 seconds
        setTimeout(() => setEmailSuccess(null), 3000);
        
        console.log('Notification email added by user:', user.email);
      }
    } catch (err) {
      console.error('Error adding email:', err);
      if (err.response && err.response.status === 409) {
        setEmailError('Sähköposti on jo lisätty tälle palautukselle');
      } else {
        setEmailError('Virhe sähköpostin lisäämisessä');
      }
    } finally {
      setEmailsLoading(prev => ({ ...prev, [returnId]: false }));
    }
  };
  
  // Function to remove a notification email
  const handleRemoveEmail = async (returnId, emailId) => {
    try {
      setEmailsLoading(prev => ({ ...prev, [returnId]: true }));
      await authenticatedApi.delete(`/api/returns/${returnId}/notification-emails/${emailId}`);
      setNotificationEmails(prev => ({
        ...prev,
        [returnId]: prev[returnId].filter(e => e.id !== emailId)
      }));
    } catch (err) {
      console.error('Error removing email:', err);
      setEmailError('Virhe sähköpostin poistamisessa');
    } finally {
      setEmailsLoading(prev => ({ ...prev, [returnId]: false }));
    }
  };
  
  // Function to handle email selection change
  const handleEmailSelectChange = (returnId, value) => {
    setSelectedEmailMap(prev => ({ ...prev, [returnId]: value }));
  };

  // Function to fetch the latest stage for each return
  const fetchLatestStagesForReturns = async (returns) => {
    const latestStagesMap = {};
    
    // Create an array of promises for fetching stages
    const stagePromises = returns.map(async (returnItem) => {
      try {
        const response = await authenticatedApi.get(`/api/returns/${returnItem.id}/stages`);
        const stages = response.data;
        
        if (stages.length > 0) {
          // Get the latest stage (last in the array since we're sorting by ASC)
          latestStagesMap[returnItem.id] = stages[stages.length - 1];
        }
      } catch (err) {
        console.error(`Error fetching stages for return ${returnItem.id}:`, err);
      }
    });
    
    // Wait for all promises to resolve
    await Promise.all(stagePromises);
    
    // Update the state with the latest stages
    setLatestStages(latestStagesMap);
  };

  // Function to handle status filter change
  const handleStatusFilterChange = (event, newValue) => {
    setStatusFilter(newValue);
    setPage(1); // Reset to first page when filter changes
  };
  
  // Function to get the effective status of a return (latest stage status if available, otherwise return status)
  const getEffectiveStatus = (returnItem) => {
    if (latestStages[returnItem.id]) {
      return latestStages[returnItem.id].status;
    }
    return returnItem.status;
  };

  // Function to handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  
  // Function to handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by a new field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Function to handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when search changes
  };
  
  // Function to handle replacement product sent filter toggle
  const handleReplacementProductSentToggle = () => {
    setShowReplacementProductSent(!showReplacementProductSent);
    setPage(1); // Reset to first page when filter changes
  };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI');
  };

  // Function to get status chip with appropriate color
  const getStatusChip = (status) => {
    let color = 'default';
    let label = status || 'Tuntematon';

    switch(status) {
      case 'pending':
        color = 'warning';
        label = 'Käsittelyssä';
        break;
      case 'completed':
        color = 'success';
        label = 'Valmis';
        break;
      case 'rejected':
        color = 'error';
        label = 'Hylätty';
        break;
      default:
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  // Function to handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Haluatko varmasti poistaa tämän palautuksen?')) {
      try {
        await authenticatedApi.delete(`/api/returns/${id}`);
        // Refresh the list and stats after deletion
        fetchReturns();
        fetchStats();
      } catch (err) {
        console.error('Error deleting return:', err);
        setReturnsError('Virhe palautuksen poistossa. Yritä myöhemmin uudelleen.');
      }
    }
  };
  
  // Function to open the new stage dialog
  const handleOpenNewStageDialog = (returnId, e) => {
    e.stopPropagation(); // Prevent row expansion
    setSelectedReturnId(returnId);
    setNewStageDialogOpen(true);
    setStageError(null);
    setStageSuccess(null);
    setStageFormData({
      description: '',
      status: 'in_progress',
      handler: '',
      notes: ''
    });
  };
  
  // Function to close the new stage dialog
  const handleCloseNewStageDialog = () => {
    setNewStageDialogOpen(false);
    setSelectedReturnId(null);
  };
  
  // Function to handle stage form changes
  const handleStageFormChange = (e) => {
    const { name, value } = e.target;
    setStageFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Function to submit a new stage
  const handleStageSubmit = async (e) => {
    e.preventDefault();
    
    // No need to validate description as it's no longer in the UI
    // We'll set a default description based on the status
    
    try {
      setStagesLoading(true);
      setStageError(null);
      
      if (!user?.email) {
        setStageError('Käyttäjän sähköpostia ei löydy. Kirjaudu uudelleen sisään.');
        return;
      }
      
      // Create a default description based on the status
      const getDefaultDescription = (status) => {
        switch(status) {
          case 'initial': return 'Palautus aloitettu';
          case 'in_progress': return 'Palautus käsittelyssä';
          case 'waiting': return 'Palautus odottaa';
          case 'returned': return 'Tuote palautunut';
          case 'completed': return 'Palautus valmis';
          case 'rejected': return 'Palautus hylätty';
          default: return 'Palautuksen tila päivitetty';
        }
      };
      
      // Remove handler from stageFormData as it will be set automatically on the server
      // Add default description based on the status
      const { handler, ...stageDataWithoutHandler } = stageFormData;
      const dataToSend = {
        ...stageDataWithoutHandler,
        description: getDefaultDescription(stageFormData.status)
      };
      
      // Include email as query parameter
      const response = await authenticatedApi.post(
        `/api/returns/${selectedReturnId}/stages?email=${encodeURIComponent(user.email)}`, 
        dataToSend
      );
      
      console.log('Stage added with default description:', dataToSend.description);
      
      console.log('Stage added with handler (email):', user.email);
      
      setStageSuccess('Uusi vaihe lisätty onnistuneesti!');
      
      // Refresh the returns and stages
      await fetchReturns();
      await fetchStats();
      
      if (expandedReturnId === selectedReturnId) {
        await fetchReturnStages(selectedReturnId);
      }
      
      setTimeout(() => {
        setNewStageDialogOpen(false);
        setStageSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error adding stage:', err);
      setStageError('Virhe vaiheen lisäämisessä. Yritä myöhemmin uudelleen.');
    } finally {
      setStagesLoading(false);
    }
  };
  
  // Function to handle expanding/collapsing a return row
  const handleRowClick = (returnId) => {
    if (expandedReturnId === returnId) {
      setExpandedReturnId(null);
    } else {
      setExpandedReturnId(returnId);
      fetchReturnStages(returnId);
      fetchNotificationEmails(returnId);
    }
  };

  // Function to update payment status
  const handleUpdatePayment = async (returnId, newPaymentStatus) => {
    try {
      // Find the return in the current list
      const returnToUpdate = returns.find(item => item.id === returnId);
      if (!returnToUpdate) return;
      
      // Update only the payment_refunded field
      await api.put(`/api/returns/${returnId}`, {
        ...returnToUpdate,
        payment_refunded: newPaymentStatus
      });
      
      // Update the returns list with the new payment status
      setReturns(prevReturns => 
        prevReturns.map(item => 
          item.id === returnId 
            ? { ...item, payment_refunded: newPaymentStatus } 
            : item
        )
      );
      
      // Show success message (optional)
      alert(newPaymentStatus ? 'Maksu merkitty palautetuksi' : 'Maksu merkitty palauttamattomaksi');
      
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Virhe maksun tilan päivityksessä. Yritä myöhemmin uudelleen.');
    }
  };
  
  // Function to update requires refund status
  const handleUpdateRequiresRefund = async (returnId, newRequiresRefundStatus) => {
    try {
      // Find the return in the current list
      const returnToUpdate = returns.find(item => item.id === returnId);
      if (!returnToUpdate) return;
      
      // Update only the requires_refund field
      await api.put(`/api/returns/${returnId}`, {
        ...returnToUpdate,
        requires_refund: newRequiresRefundStatus
      });
      
      // Update the returns list with the new requires_refund status
      setReturns(prevReturns => 
        prevReturns.map(item => 
          item.id === returnId 
            ? { ...item, requires_refund: newRequiresRefundStatus } 
            : item
        )
      );
      
      // Show success message
      alert(newRequiresRefundStatus ? 'Merkitty: Vaatii rahanpalautuksen' : 'Merkitty: Ei vaadi rahanpalautusta');
      
    } catch (err) {
      console.error('Error updating requires refund status:', err);
      alert('Virhe rahanpalautuksen tilan päivityksessä. Yritä myöhemmin uudelleen.');
    }
  };

  // Function to update exchange unit status
  const handleUpdateExchangeUnit = async (returnId, newExchangeUnitStatus) => {
    try {
      // Find the return in the current list
      const returnToUpdate = returns.find(item => item.id === returnId);
      if (!returnToUpdate) return;
      
      // Update only the is_exchange_unit field
      await api.put(`/api/returns/${returnId}`, {
        ...returnToUpdate,
        is_exchange_unit: newExchangeUnitStatus
      });
      
      // Update the returns list with the new is_exchange_unit status
      setReturns(prevReturns => 
        prevReturns.map(item => 
          item.id === returnId 
            ? { ...item, is_exchange_unit: newExchangeUnitStatus } 
            : item
        )
      );
      
      // Show success message
      alert(newExchangeUnitStatus ? 'Merkitty: Vaihtoyksikkö' : 'Merkitty: Ei vaihtoyksikkö');
      
    } catch (err) {
      console.error('Error updating exchange unit status:', err);
      alert('Virhe vaihtoyksikön tilan päivityksessä. Yritä myöhemmin uudelleen.');
    }
  };
  
  // Function to update replacement product sent status
  const handleUpdateReplacementProductSent = async (returnId, newReplacementProductSentStatus) => {
    try {
      // Find the return in the current list
      const returnToUpdate = returns.find(item => item.id === returnId);
      if (!returnToUpdate) return;
      
      // Update only the replacement_product_sent field
      await authenticatedApi.put(`/api/returns/${returnId}`, {
        ...returnToUpdate,
        replacement_product_sent: newReplacementProductSentStatus
      });
      
      // Update the returns list with the new replacement_product_sent status
      setReturns(prevReturns => 
        prevReturns.map(item => 
          item.id === returnId 
            ? { ...item, replacement_product_sent: newReplacementProductSentStatus } 
            : item
        )
      );
      
      // Show success message
      alert(newReplacementProductSentStatus ? 'Merkitty: Korvaava tuote lähetetty' : 'Merkitty: Korvaavaa tuotetta ei ole lähetetty');
      
    } catch (err) {
      console.error('Error updating replacement product sent status:', err);
      alert('Virhe korvaavan tuotteen lähetystilan päivityksessä. Yritä myöhemmin uudelleen.');
    }
  };

  // Function to open the return date dialog
  const handleOpenReturnDateDialog = (returnId, e) => {
    if (e) e.stopPropagation();
    const returnItem = returns.find(item => item.id === returnId);
    if (returnItem) {
      setSelectedReturnForDate(returnId);
      setSelectedDate(returnItem.return_date ? new Date(returnItem.return_date) : new Date());
      setReturnDateDialogOpen(true);
    }
  };
  
  // Function to close the return date dialog
  const handleCloseReturnDateDialog = () => {
    setReturnDateDialogOpen(false);
    setSelectedReturnForDate(null);
    setSelectedDate(null);
  };
  
  // Function to update the return date
  const handleUpdateReturnDate = async () => {
    if (!selectedReturnForDate || !selectedDate) return;
    
    try {
      setUpdatingReturnDate(true);
      
      // Format the date for the API
      const formattedDate = selectedDate.toISOString().split('T')[0];
      
      // Get the current return data
      const returnToUpdate = returns.find(r => r.id === selectedReturnForDate);
      
      if (!returnToUpdate) {
        console.error('Return not found');
        return;
      }
      
      // Update only the return_date field
      const updatedReturn = {
        ...returnToUpdate,
        return_date: formattedDate
      };
      
      // Send the update to the API
      await authenticatedApi.put(`/api/returns/${selectedReturnForDate}`, updatedReturn);
      
      // Update the local state
      setReturns(prevReturns => 
        prevReturns.map(r => 
          r.id === selectedReturnForDate ? { ...r, return_date: formattedDate } : r
        )
      );
      
      // Close the dialog
      setReturnDateDialogOpen(false);
      setSelectedReturnForDate(null);
      setSelectedDate(null);
      
    } catch (error) {
      console.error('Error updating return date:', error);
    } finally {
      setUpdatingReturnDate(false);
    }
  };
  
  // Function to toggle the important status of a return
  const handleToggleImportant = async (returnId, e) => {
    // Prevent row expansion when clicking the star
    if (e) e.stopPropagation();
    
    try {
      // Find the return to update
      const returnToUpdate = returns.find(r => r.id === returnId);
      
      if (!returnToUpdate) {
        console.error('Return not found');
        return;
      }
      
      // Toggle the important status
      const updatedReturn = {
        ...returnToUpdate,
        is_important: !returnToUpdate.is_important
      };
      
      // Send the update to the API
      await authenticatedApi.put(`/api/returns/${returnId}`, updatedReturn);
      
      // Update the local state
      setReturns(prevReturns => 
        prevReturns.map(r => 
          r.id === returnId ? { ...r, is_important: !r.is_important } : r
        )
      );
      
    } catch (error) {
      console.error('Error updating important status:', error);
    }
  };

  // Function to fetch return stages
  const fetchReturnStages = async (returnId) => {
    try {
      setStagesLoading(true);
      const response = await authenticatedApi.get(`/api/returns/${returnId}/stages`);
      setReturnStages(response.data);
    } catch (err) {
      console.error('Error fetching return stages:', err);
      // We don't show an error message here to keep the UI clean
      setReturnStages([]);
    } finally {
      setStagesLoading(false);
    }
  };
  
  // Function to format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fi-FI');
  };
  
  // Function to get status label for stages
  const getStageStatusLabel = (status) => {
    switch(status) {
      case 'initial': return 'Aloitettu';
      case 'in_progress': return 'Käsittelyssä';
      case 'waiting': return 'Odottaa';
      case 'returned': return 'Palautunut';
      case 'completed': return 'Valmis';
      default: return status || 'Tuntematon';
    }
  };
  
  // Function to get status color for stages
  const getStageStatusColor = (status) => {
    switch(status) {
      case 'initial': return 'info';
      case 'in_progress': return 'warning';
      case 'waiting': return 'secondary';
      case 'returned': return 'warning'; // Changed from 'primary' (blue) to 'warning' (orange)
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  // Function to check if a return matches the search term
  const matchesSearch = (returnItem) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Check if the latest stage description matches the search term
    const latestStageMatches = latestStages[returnItem.id] && 
      latestStages[returnItem.id].description && 
      latestStages[returnItem.id].description.toLowerCase().includes(searchLower);
    
    // Check if the latest stage handler matches the search term
    const latestStageHandlerMatches = latestStages[returnItem.id] && 
      latestStages[returnItem.id].handler && 
      latestStages[returnItem.id].handler.toLowerCase().includes(searchLower);
    
    // Search in all text fields
    return (
      (returnItem.customer_name && returnItem.customer_name.toLowerCase().includes(searchLower)) ||
      (returnItem.seller_name && returnItem.seller_name.toLowerCase().includes(searchLower)) ||
      (returnItem.order_number && returnItem.order_number.toLowerCase().includes(searchLower)) ||
      (returnItem.invoice_number && returnItem.invoice_number.toLowerCase().includes(searchLower)) ||
      (returnItem.items_to_return && returnItem.items_to_return.toLowerCase().includes(searchLower)) ||
      (returnItem.information && returnItem.information.toLowerCase().includes(searchLower)) ||
      (returnItem.additional_info && returnItem.additional_info.toLowerCase().includes(searchLower)) ||
      (returnItem.account_number && returnItem.account_number.toLowerCase().includes(searchLower)) ||
      latestStageMatches ||
      latestStageHandlerMatches
    );
  };
  
  // Get filtered returns based on status filter and search term
  const filteredReturns = returns.filter(returnItem => {
    const statusMatches = statusFilter === 'all' || getEffectiveStatus(returnItem) === statusFilter;
    const searchMatches = matchesSearch(returnItem);
    const replacementProductSentMatches = !showReplacementProductSent || returnItem.replacement_product_sent === true;
    return statusMatches && searchMatches && replacementProductSentMatches;
  });
  
  // Sort the filtered returns
  const sortedReturns = [...filteredReturns].sort((a, b) => {
    // Handle latestStage sorting
    if (sortField === 'latestStage') {
      const stageA = latestStages[a.id];
      const stageB = latestStages[b.id];
      
      // Handle cases where one or both returns don't have stages
      if (!stageA && !stageB) return 0;
      if (!stageA) return 1;
      if (!stageB) return -1;
      
      // Sort by stage description
      return sortDirection === 'asc'
        ? stageA.description.localeCompare(stageB.description)
        : stageB.description.localeCompare(stageA.description);
    }
    
    // Handle null values
    if (a[sortField] === null && b[sortField] === null) return 0;
    if (a[sortField] === null) return 1;
    if (b[sortField] === null) return -1;
    
    // Handle date fields
    if (['notification_date', 'return_date'].includes(sortField)) {
      const dateA = a[sortField] ? new Date(a[sortField]).getTime() : 0;
      const dateB = b[sortField] ? new Date(b[sortField]).getTime() : 0;
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    // Handle text fields
    if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
      return sortDirection === 'asc' 
        ? a[sortField].localeCompare(b[sortField]) 
        : b[sortField].localeCompare(a[sortField]);
    }
    
    // Handle boolean fields
    if (typeof a[sortField] === 'boolean') {
      return sortDirection === 'asc' 
        ? (a[sortField] === b[sortField] ? 0 : a[sortField] ? 1 : -1)
        : (a[sortField] === b[sortField] ? 0 : a[sortField] ? -1 : 1);
    }
    
    // Handle numeric fields
    return sortDirection === 'asc' ? a[sortField] - b[sortField] : b[sortField] - a[sortField];
  });
  
  // Get paginated returns
  const paginatedReturns = sortedReturns.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  
  const pageCount = Math.ceil(filteredReturns.length / rowsPerPage);

  // Fetch statistics from the backend
  const fetchStats = async () => {
    try {
      const response = await authenticatedApi.get('/api/returns/stats');
      setStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Virhe tilastojen hakemisessa. Yritä myöhemmin uudelleen.');
      setLoading(false);
    }
  };
  
  // Fetch data when component mounts
  useEffect(() => {
    fetchStats();
    fetchReturns();
    fetchAvailableEmails();
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {/* Header with Stats and New Return Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Palautukset</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to="/new"
          startIcon={<AddIcon />}
        >
          Uusi palautus
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Stats Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3, flex: 1, minWidth: '200px' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Kaikki palautukset
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Typography variant="h3">{stats.totalReturns}</Typography>
          )}
        </Paper>
        
        <Paper elevation={2} sx={{ p: 3, flex: 1, minWidth: '200px' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Käsittelyssä olevat
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Typography variant="h3">{stats.pendingReturns}</Typography>
          )}
        </Paper>
        
        <Paper elevation={2} sx={{ p: 3, flex: 1, minWidth: '200px' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Valmiit palautukset
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Typography variant="h3">{stats.completedReturns}</Typography>
          )}
        </Paper>
      </Box>
      
      {/* Search field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Hae palautuksia"
          placeholder="Hae asiakkaalla, myyjällä, tilausnumerolla, laskunumerolla..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton
                onClick={() => setSearchTerm('')}
                edge="end"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }}
        />
      </Box>
      
      {/* Filter tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={statusFilter} 
          onChange={handleStatusFilterChange}
          aria-label="return status filter tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Kaikki" value="all" />
          <Tab label="Aloitettu" value="initial" />
          <Tab label="Käsittelyssä" value="in_progress" />
          <Tab label="Odottaa" value="waiting" />
          <Tab label="Palautunut" value="returned" />
          <Tab label="Valmis" value="completed" />
          <Tab label="Hylätty" value="rejected" />
        </Tabs>
      </Box>
      
      {/* Returns List */}
      {returnsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredReturns.length === 0 ? (
        <Typography sx={{ p: 2 }}>
          Ei palautuksia tässä kategoriassa.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ width: '100%', overflow: 'visible' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell width="40px"></TableCell>
                  <TableCell 
                    width="40px" 
                    align="center" 
                    sx={{ px: 1, cursor: 'pointer' }}
                    onClick={() => handleSort('is_important')}
                  >
                    Tärkeä
                    {sortField === 'is_important' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    width="180px" 
                    align="center" 
                    sx={{ px: 1, cursor: 'pointer' }}
                    onClick={() => handleSort('requires_refund')}
                  >
                    Vaatii rahanpalautuksen
                    {sortField === 'requires_refund' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    width="120px" 
                    align="center" 
                    sx={{ px: 1, cursor: 'pointer' }}
                    onClick={() => handleSort('is_exchange_unit')}
                  >
                    Vaihtoyksikkö
                    {sortField === 'is_exchange_unit' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    width="180px" 
                    align="center" 
                    sx={{ px: 1, cursor: 'pointer' }}
                    onClick={() => handleSort('replacement_product_sent')}
                  >
                    Korvaava tuote lähetetty
                    {sortField === 'replacement_product_sent' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('customer_name')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Asiakas
                    {sortField === 'customer_name' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('invoice_number')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Laskunumero
                    {sortField === 'invoice_number' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('order_number')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Tilausnumero
                    {sortField === 'order_number' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('notification_date')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Ilmoitus pvm
                    {sortField === 'notification_date' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('return_date')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Palautus pvm
                    {sortField === 'return_date' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('status')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Tila
                    {sortField === 'status' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('seller_name')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Myyjä
                    {sortField === 'seller_name' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('items_to_return')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Mitä palautetaan
                    {sortField === 'items_to_return' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('latestStage')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Viimeisin vaihe
                    {sortField === 'latestStage' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>Toiminnot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReturns.map((returnItem) => (
                  <React.Fragment key={returnItem.id}>
                    <TableRow 
                      hover 
                      onClick={() => handleRowClick(returnItem.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                      selected={expandedReturnId === returnItem.id}
                    >
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(returnItem.id);
                          }}
                        >
                          {expandedReturnId === returnItem.id ? 
                            <KeyboardArrowUpIcon /> : 
                            <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          aria-label={returnItem.is_important ? "Poista tärkeä merkintä" : "Merkitse tärkeäksi"}
                          size="small"
                          onClick={(e) => handleToggleImportant(returnItem.id, e)}
                          color={returnItem.is_important ? "warning" : "default"}
                          title={returnItem.is_important ? "Poista tärkeä merkintä" : "Merkitse tärkeäksi"}
                        >
                          {returnItem.is_important ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {returnItem.requires_refund && (
                            <MonetizationOnIcon color="error" fontSize="small" titleAccess="Vaatii rahanpalautuksen" sx={{ mr: 0.5 }} />
                          )}
                          <Typography variant="body2">
                            {returnItem.requires_refund ? 'Kyllä' : 'Ei'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {returnItem.is_exchange_unit ? 'Kyllä' : 'Ei'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {returnItem.replacement_product_sent ? 'Kyllä' : 'Ei'}
                        </Typography>
                      </TableCell>
                      <TableCell>{returnItem.customer_name || '-'}</TableCell>
                      <TableCell>{returnItem.invoice_number || '-'}</TableCell>
                      <TableCell>{returnItem.order_number || '-'}</TableCell>
                      <TableCell>{formatDate(returnItem.notification_date)}</TableCell>
                      <TableCell>{formatDate(returnItem.return_date)}</TableCell>
                      <TableCell>
                        {latestStages[returnItem.id] ? (
                          <Chip 
                            label={getStageStatusLabel(latestStages[returnItem.id].status)}
                            color={getStageStatusColor(latestStages[returnItem.id].status)}
                            size="small"
                          />
                        ) : (
                          getStatusChip(returnItem.status)
                        )}
                      </TableCell>
                      <TableCell>{returnItem.seller_name || '-'}</TableCell>
                      <TableCell>{returnItem.items_to_return || '-'}</TableCell>
                      <TableCell>
                        {latestStages[returnItem.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {latestStages[returnItem.id].description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {latestStages[returnItem.id].handler && `Käsittelijä: ${latestStages[returnItem.id].handler} | `}
                              {formatDateTime(latestStages[returnItem.id].stage_date)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Ei vaiheita
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            component={Link} 
                            to={`/edit/${returnItem.id}`}
                            color="primary"
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            title="Muokkaa palautusta"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={(e) => handleOpenNewStageDialog(returnItem.id, e)}
                            color="success"
                            size="small"
                            title="Lisää uusi vaihe"
                          >
                            <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>+</Box>
                          </IconButton>
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(returnItem.id);
                            }}
                            color="error"
                            size="small"
                            title="Poista palautus"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                      
                      {/* Expandable row for return details and stages */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, width: '100%' }} colSpan={12}>
                          <Collapse in={expandedReturnId === returnItem.id} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, width: '100%', maxWidth: '100%', backgroundColor: '#f5f9ff', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                              {/* Return Details Section in a single box with three columns */}
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <Typography variant="h6" gutterBottom component="div">
                                    Palautuksen tiedot
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                </Grid>
                                
                                <Grid item xs={12}>
                                  <Paper sx={{ p: 3 }}>
                                    <Grid container spacing={4}>
                                      {/* First column - Basic details */}
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
                                          Perustiedot
                                        </Typography>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Asiakas</Typography>
                                          <Typography variant="body1">{returnItem.customer_name || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Myyjä</Typography>
                                          <Typography variant="body1">{returnItem.seller_name || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Tilausnumero</Typography>
                                          <Typography variant="body1">{returnItem.order_number || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Laskunumero</Typography>
                                          <Typography variant="body1">{returnItem.invoice_number || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Tilinumero</Typography>
                                          <Typography variant="body1" sx={{ 
                                            bgcolor: returnItem.account_number ? 'rgba(255, 235, 59, 0.2)' : 'inherit',
                                            p: returnItem.account_number ? 1 : 0,
                                            borderRadius: 1
                                          }}>
                                            {returnItem.account_number || '-'}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      
                                      {/* Second column - Return details */}
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
                                          Palautuksen tiedot
                                        </Typography>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Mitä palautetaan</Typography>
                                          <Typography variant="body1">{returnItem.items_to_return || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Tiedot</Typography>
                                          <Typography variant="body1">{returnItem.information || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" color="text.secondary">Lisätiedot</Typography>
                                          <Typography variant="body1">{returnItem.additional_info || '-'}</Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <Box>
                                            <Typography variant="subtitle2" color="text.secondary">Tuote palautunut pvm</Typography>
                                            <Typography variant="body1">{formatDate(returnItem.return_date)}</Typography>
                                          </Box>
                                          <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small"
                                            onClick={(e) => handleOpenReturnDateDialog(returnItem.id, e)}
                                          >
                                            {returnItem.return_date ? 'Muokkaa' : 'Lisää'}
                                          </Button>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                          <Box>
                                            <Typography variant="subtitle2" color="text.secondary">Maksu palautettu</Typography>
                                            <Typography variant="body1">
                                              {returnItem.payment_refunded ? 'Kyllä' : 'Ei'}
                                            </Typography>
                                          </Box>
                                          <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdatePayment(returnItem.id, !returnItem.payment_refunded);
                                            }}
                                          >
                                            {returnItem.payment_refunded ? 'Merkitse ei' : 'Merkitse kyllä'}
                                          </Button>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>Vaatii rahanpalautuksen</Typography>
                                            {returnItem.requires_refund && (
                                              <MonetizationOnIcon color="error" fontSize="small" />
                                            )}
                                            <Typography variant="body1" sx={{ ml: 1 }}>
                                              {returnItem.requires_refund ? 'Kyllä' : 'Ei'}
                                            </Typography>
                                          </Box>
                                          <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdateRequiresRefund(returnItem.id, !returnItem.requires_refund);
                                            }}
                                          >
                                            {returnItem.requires_refund ? 'Merkitse ei' : 'Merkitse kyllä'}
                                          </Button>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <Box>
                                            <Typography variant="subtitle2" color="text.secondary">Vaihtoyksikkö</Typography>
                                            <Typography variant="body1">
                                              {returnItem.is_exchange_unit ? 'Kyllä' : 'Ei'}
                                            </Typography>
                                          </Box>
                                          <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdateExchangeUnit(returnItem.id, !returnItem.is_exchange_unit);
                                            }}
                                          >
                                            {returnItem.is_exchange_unit ? 'Merkitse ei' : 'Merkitse kyllä'}
                                          </Button>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                                          <Box>
                                            <Typography variant="subtitle2" color="text.secondary">Korvaava tuote lähetetty</Typography>
                                            <Typography variant="body1">
                                              {returnItem.replacement_product_sent ? 'Kyllä' : 'Ei'}
                                            </Typography>
                                          </Box>
                                          <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdateReplacementProductSent(returnItem.id, !returnItem.replacement_product_sent);
                                            }}
                                          >
                                            {returnItem.replacement_product_sent ? 'Merkitse ei' : 'Merkitse kyllä'}
                                          </Button>
                                        </Box>
                                      </Grid>
                                      
                                      {/* Third column - Email notifications */}
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mb: 2, display: 'flex', alignItems: 'center' }}>
                                          <EmailIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                          Sähköposti-ilmoitukset
                                        </Typography>
                                        
                                        {/* Email selection dropdown */}
                                        <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id={`email-select-label-${returnItem.id}`}>Valitse sähköposti</InputLabel>
                                            <Select
                                              labelId={`email-select-label-${returnItem.id}`}
                                              value={selectedEmailMap[returnItem.id] || ''}
                                              onChange={(e) => handleEmailSelectChange(returnItem.id, e.target.value)}
                                              label="Valitse sähköposti"
                                              disabled={emailsLoading[returnItem.id]}
                                            >
                                              <MenuItem value=""><em>Valitse...</em></MenuItem>
                                              {availableEmails.map(email => (
                                                <MenuItem key={email.id} value={email.email}>{email.email}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleAddEmail(returnItem.id)}
                                            disabled={emailsLoading[returnItem.id]}
                                            sx={{ minWidth: '80px' }}
                                          >
                                            {emailsLoading[returnItem.id] ? <CircularProgress size={20} /> : 'Lisää'}
                                          </Button>
                                        </Box>
                                        
                                        {emailError && (
                                          <Alert severity="error" sx={{ mb: 2 }}>
                                            {emailError}
                                          </Alert>
                                        )}
                                        
                                        {emailSuccess && (
                                          <Alert severity="success" sx={{ mb: 2 }}>
                                            {emailSuccess}
                                          </Alert>
                                        )}
                                        
                                        {/* Email list */}
                                        <List sx={{ bgcolor: 'background.paper', borderRadius: 1, maxHeight: '220px', overflow: 'auto' }}>
                                          {emailsLoading[returnItem.id] && !notificationEmails[returnItem.id] ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                              <CircularProgress size={24} />
                                            </Box>
                                          ) : notificationEmails[returnItem.id] && notificationEmails[returnItem.id].length > 0 ? (
                                            notificationEmails[returnItem.id].map((emailObj, index) => {
                                              const isInitiatorEmail = returnItem.initiator_email && emailObj.email === returnItem.initiator_email;
                                              return (
                                                <ListItem 
                                                  key={emailObj.id} 
                                                  sx={{
                                                    bgcolor: isInitiatorEmail ? 'rgba(33, 150, 243, 0.1)' : 'inherit',
                                                    borderRadius: 1,
                                                    mb: 0.5
                                                  }}
                                                >
                                                  <ListItemText 
                                                    primary={emailObj.email}
                                                    secondary={isInitiatorEmail ? 'Palautuksen aloittaja' : null}
                                                  />
                                                  <ListItemSecondaryAction>
                                                    <IconButton 
                                                      edge="end" 
                                                      aria-label="delete"
                                                      onClick={() => handleRemoveEmail(returnItem.id, emailObj.id)}
                                                      disabled={isInitiatorEmail || emailsLoading[returnItem.id]}
                                                      color="error"
                                                      size="small"
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </ListItemSecondaryAction>
                                                </ListItem>
                                              );
                                            })
                                          ) : (
                                            <ListItem>
                                              <ListItemText primary="Ei sähköposteja" secondary="Lisää sähköposteja yllä olevasta valikosta" />
                                            </ListItem>
                                          )}
                                        </List>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>
                              </Grid>

                              <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="h6" component="div" sx={{ mr: 2 }}>
                                  Palautuksen vaiheet
                                </Typography>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenNewStageDialog(returnItem.id, e);
                                  }}
                                >
                                  Lisää vaihe
                                </Button>

                              </Box>
                              
                              {stagesLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                  <CircularProgress size={30} />
                                </Box>
                              ) : returnStages.length === 0 ? (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', my: 2 }}>
                                  Ei vaiheita tälle palautukselle.
                                </Typography>
                              ) : (
                                <Stepper 
                                  orientation="vertical" 
                                  sx={{ mb: 2, width: '100%' }} 
                                  activeStep={returnStages.length - 1}
                                >
                                  {returnStages.map((stage, index) => (
                                    <Step key={stage.id} expanded active completed={index < returnStages.length - 1}>
                                      <StepLabel 
                                        optional={
                                          <Typography variant="caption">
                                            {formatDateTime(stage.stage_date)}
                                          </Typography>
                                        }
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="body1">{stage.description}</Typography>
                                          <Chip 
                                            size="small" 
                                            label={getStageStatusLabel(stage.status)} 
                                            color={getStageStatusColor(stage.status)} 
                                          />
                                        </Box>
                                      </StepLabel>
                                      <StepContent>
                                        <Box sx={{ mb: 2 }}>
                                          {stage.handler && (
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                              Käsittelijä: {stage.handler}
                                            </Typography>
                                          )}
                                          {stage.notes && (
                                            <Typography variant="body2">
                                              {stage.notes}
                                            </Typography>
                                          )}
                                        </Box>
                                      </StepContent>
                                    </Step>
                                  ))}
                                </Stepper>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Pagination 
                count={pageCount} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          </>
        )}
      
      {/* Dialog for adding a new stage */}
      <Dialog open={newStageDialogOpen} onClose={handleCloseNewStageDialog} maxWidth="md" fullWidth>
        <DialogTitle>Lisää uusi vaihe palautukselle</DialogTitle>
        <DialogContent>
          {stageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {stageError}
            </Alert>
          )}
          {stageSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {stageSuccess}
            </Alert>
          )}
          <Box component="form" onSubmit={handleStageSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Description field removed as requested */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="stage-status-label">Tila</InputLabel>
                  <Select
                    labelId="stage-status-label"
                    name="status"
                    value={stageFormData.status}
                    onChange={handleStageFormChange}
                    label="Tila"
                    disabled={stagesLoading}
                  >
                    <MenuItem value="initial">Aloitettu</MenuItem>
                    <MenuItem value="in_progress">Käsittelyssä</MenuItem>
                    <MenuItem value="waiting">Odottaa</MenuItem>
                    <MenuItem value="returned">Palautunut</MenuItem>
                    <MenuItem value="completed">Valmis</MenuItem>
                    <MenuItem value="rejected">Hylätty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Handler field removed - will use Auth0 email automatically */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lisätiedot"
                  name="notes"
                  multiline
                  rows={3}
                  value={stageFormData.notes}
                  onChange={handleStageFormChange}
                  disabled={stagesLoading}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewStageDialog} disabled={stagesLoading}>
            Peruuta
          </Button>
          <Button 
            onClick={handleStageSubmit} 
            variant="contained" 
            color="primary"
            disabled={stagesLoading}
          >
            {stagesLoading ? 'Tallennetaan...' : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Date Dialog */}
      <Dialog open={returnDateDialogOpen} onClose={handleCloseReturnDateDialog}>
        <DialogTitle>Tuote palautunut päivämäärä</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="dd.MM.yyyy"
                customInput={<TextField fullWidth label="Palautuspäivämäärä" />}
              />
              <FormHelperText>Valitse päivämäärä jolloin tuote on palautunut</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReturnDateDialog} color="inherit">
            Peruuta
          </Button>
          <Button 
            onClick={handleUpdateReturnDate} 
            color="primary" 
            variant="contained"
            disabled={updatingReturnDate}
          >
            {updatingReturnDate ? <CircularProgress size={24} /> : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Home;
